import React, { useState, useEffect } from 'react';
import { Box, Text, Button, Flex, HStack, Spinner, useToast } from '@chakra-ui/react';
import { FaCaretRight, FaTimes } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { verifyOTP_Register } from '../apis/verifyApi';
import { useAuth } from '../context/AuthContext';

const VerifyRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, checkAuth } = useAuth();
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  
  // Get state from location
  const locationState = location.state || {};
  const { 
    email, 
    membershipType, 
    requiresPayment, 
    name,
    amount
  } = locationState;

  // Log the received state for debugging
  useEffect(() => {
    console.log("VerifyRegister received state:", locationState);
  }, [locationState]);

  // Check if user is already authenticated and redirect to dashboard
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleChange = (otp) => {
    setOtp(otp);
  };

  const handleClear = () => {
    setOtp('');
  };

  const handleSubmit = async () => {
    if (otp.length === 6) {
      setIsSubmitting(true);
      try {
        console.log("Submitting OTP verification with:", { 
          otp, 
          membershipType
        });
        
        const response = await verifyOTP_Register(otp);
        await checkAuth(); // Call checkAuth to get the access token
        
        // Show success toast
        toast({
          title: "Verification successful",
          description: requiresPayment 
            ? "Your account has been verified. Please complete payment to activate your membership." 
            : "Your account has been verified successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        
        // If unlimited membership, redirect to payment page
        if (requiresPayment) {
          navigate('/payment', {
            state: {
              email,
              name,
              membershipType,
              amount,
              verified: true,
              redirectPath: '/dashboard'
            }
          });
        } else {
          // For basic membership, go to dashboard
          navigate(response.redirectPath || '/dashboard');
        }
      } catch (error) {
        console.error("Verification error:", error);
        // Show error toast
        toast({
          title: "Verification failed",
          description: error.message || "There was an error verifying your OTP. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Show warning toast for incomplete OTP
      toast({
        title: "Incomplete OTP",
        description: "Please enter all 6 digits of the verification code.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleResend = () => {
    
    
    toast({
      title: "OTP Resent",
      description: "A new verification code has been sent to your email address.",
      status: "info",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="#82ca9c" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Flex height="auto" alignItems="center" justifyContent="center">
      <Box 
        className="container" 
        p={8} 
        bg="white" 
        borderRadius="25px" 
        w={{ base: "90%", md: "350px" }} 
        mx="auto"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
        border="1px solid #E2E8F0"
        marginTop={45}
      >
        <Text className="title" fontSize={{ base: "24px", md: "28px" }} fontWeight="700" color="#093030" mb={6}>
          OTP Verification
        </Text>
        <Text className="sub-title" color="#B5BAB8" fontSize={{ base: "12px", md: "14px" }} mb={6}>
          <Text as="span" className="phone-number" display="block" color="#093030" fontWeight="600">
            A verification code has been sent to your email address. Please enter your code to verify.
            {requiresPayment && " After verification, you'll be directed to complete payment for your unlimited membership."}
          </Text>
        </Text>
          <OtpInput
            value={otp}
            onChange={handleChange}
            numInputs={6}
            renderSeparator={<span style={{ margin: "0 4px" }}>-</span>}
            renderInput={(props) => (
              <input
                {...props}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    props.onChange(e);
                  }
                }}
              />
            )}
            inputStyle={{
              width: '2.2rem',
              height: '2.5rem',
              margin: '0 0.25rem',
              fontSize: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(0,0,0,0.2)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'all 0.2s ease-in-out',
              textAlign: 'center',
              fontWeight: '600',
              color: '#093030',
            }}
            containerStyle={{
              justifyContent: 'center',
            }}
          />
        <HStack mt={4} justifyContent="space-between">
          <Button 
            className="resend" 
            onClick={handleResend} 
            bg="transparent" 
            fontWeight="600" 
            color="#3DAFE1"
            _hover={{ color: "#2C9BC9" }}
            size="sm"
          >
            Resend OTP
            <FaCaretRight style={{ marginLeft: '5px' }} />
          </Button>
          <Button
            className="clear"
            onClick={handleClear}
            bg="transparent"
            fontWeight="600"
            color="#F56565"
            _hover={{ color: "#E53E3E" }}
            size="sm"
            leftIcon={<FaTimes />}
          >
            Clear
          </Button>
        </HStack>
        <Button 
          onClick={handleSubmit} 
          mt={4} 
          bg="#82ca9c" 
          color={"#fff"}
          w="100%"
          _hover={{ bg: "#6db989" }}
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          isLoading={isSubmitting}
          loadingText="Verifying"
        >
          Submit
        </Button>
      </Box>
    </Flex>
  );
};

export default VerifyRegister;